/**
 * Question Banks — list view (search / status filter / sort + card grid).
 *
 * Extracted verbatim from QuestionBanksPage.jsx during the Phase 5 refactor
 * (no behaviour change).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { EmptyState } from '../common/EmptyState';
import { Library, Plus, Tag, Search, Link2 } from 'lucide-react';
import { STATUS_PILL_CLS, SORT_OPTIONS, ALL_STATUSES } from './banksMeta';
import { PublishProgressChip } from './PublishProgressChip';
import { BankActionsMenu } from './BankActionsMenu';

export function BankList({ banks, onCreate, onOpen, onEdit, onDuplicate, onRetire, onDelete, canEdit = true }) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(new Set()); // empty = all
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const toggleStatus = (s) => {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };

  const visible = useMemo(() => {
    let out = banks;
    if (statusFilter.size > 0) {
      out = out.filter(b => statusFilter.has(b.lifecycle_status));
    }
    if (debouncedSearch) {
      out = out.filter(b => {
        const hay = `${b.title || ''} ${b.description || ''} ${(b.tags || []).join(' ')}`.toLowerCase();
        return hay.includes(debouncedSearch);
      });
    }
    out = [...out];
    switch (sort) {
      case 'title_asc':
        out.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'most_items':
        out.sort((a, b) => (b.item_count || 0) - (a.item_count || 0));
        break;
      case 'least_pub':
        out.sort((a, b) => (a.published_item_count || 0) - (b.published_item_count || 0));
        break;
      case 'recent':
      default:
        out.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
        break;
    }
    return out;
  }, [banks, debouncedSearch, statusFilter, sort]);

  return (
    <div className="space-y-5 relative" data-testid="banks-list">
      <div aria-hidden className="page-blob bg-[var(--lumyn-banks)]" />
      <div className="relative z-10 space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-banks)]/10 text-[var(--lumyn-banks)] text-[11px] font-semibold uppercase tracking-[0.14em]">
              <Library className="w-3.5 h-3.5" />Question banks
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight">
              The vault
              <span className="ml-2 text-base font-medium text-muted-foreground">
                {banks.length} {banks.length === 1 ? 'bank' : 'banks'}
              </span>
            </h1>
            <p className="text-sm text-muted-foreground max-w-prose">
              Reusable, version-pinned question pools. Pull from any of them into any quiz.
            </p>
          </div>
          {canEdit && (
            <Button
              onClick={onCreate}
              data-testid="create-bank-btn"
              className="bg-[var(--lumyn-banks)] hover:bg-[var(--lumyn-banks)]/90 text-white shadow-md shadow-[var(--lumyn-banks)]/20"
            >
              <Plus className="w-4 h-4 mr-1" />New bank
            </Button>
          )}
        </div>

        {/* Search + sort + status chips — sticky-feeling toolbar */}
        {banks.length > 0 && (
          <div className="flex flex-wrap items-center gap-2.5 p-2.5 rounded-lg border bg-card/50" data-testid="banks-toolbar">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search banks, descriptions, tags…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="banks-search-input"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-44 text-sm" data-testid="banks-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap items-center gap-1.5">
              {ALL_STATUSES.map(s => {
                const on = statusFilter.has(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition ${
                      on
                        ? 'bg-[var(--lumyn-banks)] text-white border-[var(--lumyn-banks)] shadow-sm'
                        : 'bg-background text-muted-foreground border-border hover:border-[var(--lumyn-banks)]/40 hover:text-foreground'
                    }`}
                    data-testid={`status-filter-${s}`}
                  >
                    {s.replace('_', ' ')}
                  </button>
                );
              })}
              {statusFilter.size > 0 && (
                <button
                  onClick={() => setStatusFilter(new Set())}
                  className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                  data-testid="status-filter-clear"
                >
                  clear
                </button>
              )}
            </div>
          </div>
        )}

      {banks.length === 0 ? (
        <EmptyState icon={Library} title="No question banks yet" description="Create reusable, version-pinned question pools and pull from them in any quiz." />
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground border rounded-lg bg-card/30" data-testid="banks-no-match">
          No banks match your filters. Try clearing them.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="banks-grid">
          {visible.map(b => {
            const isEmpty = (b.item_count || 0) === 0;
            const pillCls = STATUS_PILL_CLS[b.lifecycle_status] || STATUS_PILL_CLS.draft;
            return (
              <div key={b.bank_id} className="relative group" data-testid={`bank-card-${b.bank_id}`}>
                {/* Card body — clickable to open. Kebab lives absolutely positioned
                    on top so its own clicks don't trigger the card's onOpen. */}
                <button
                  onClick={() => onOpen(b)}
                  className="text-left w-full h-full"
                  data-testid={`bank-card-open-${b.bank_id}`}
                >
                  <Card
                    className={`relative overflow-hidden hover:border-[var(--lumyn-banks)] hover:shadow-md hover:shadow-[var(--lumyn-banks)]/20 transition cursor-pointer h-full ${
                      isEmpty ? 'border-dashed' : ''
                    }`}
                  >
                    {/* Accent stripe — top edge in banks-purple. Saturates the
                        whole grid in the page theme. */}
                    <div
                      aria-hidden
                      className="absolute inset-x-0 top-0 h-1 bg-[var(--lumyn-banks)]/60 group-hover:bg-[var(--lumyn-banks)] transition-colors"
                    />
                    <CardHeader className="pb-2 pt-4">
                      <CardTitle className="text-sm flex items-center justify-between gap-2">
                        <span className="truncate pr-8">{b.title}</span>
                        <span
                          className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${pillCls}`}
                          data-testid={`bank-status-${b.bank_id}`}
                        >
                          {b.lifecycle_status.replace('_', ' ')}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-xs space-y-2">
                      {b.description ? (
                        <p className="text-muted-foreground line-clamp-2 min-h-[2.5em]">{b.description}</p>
                      ) : (
                        <p className="italic text-muted-foreground/60 line-clamp-2 min-h-[2.5em]">No description</p>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-border/60">
                        <span className="inline-flex items-center gap-2">
                          <PublishProgressChip total={b.item_count || 0} published={b.published_item_count || 0} />
                          {(b.unpublished_item_count || 0) > 0 && (
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded border font-display tabular-nums ${
                                b.lifecycle_status === 'published'
                                  ? 'border-amber-300 bg-amber-50 text-amber-800'
                                  : 'border-border text-muted-foreground'}`}
                              title={b.lifecycle_status === 'published'
                                ? 'Published bank with questions that are not yet published'
                                : 'Questions not yet published'}
                              data-testid={`bank-unpublished-badge-${b.bank_id}`}
                            >
                              {b.unpublished_item_count} unpublished
                            </span>
                          )}
                        </span>
                        {b.client_id && (
                          <span className="text-[10px] bg-[var(--lumyn-banks)]/10 text-[var(--lumyn-banks)] px-1.5 py-0.5 rounded">
                            {b.client_id}
                          </span>
                        )}
                      </div>
                      {typeof b.usage_count === 'number' && b.usage_count > 0 && (
                        <div
                          className="flex items-center gap-1 text-[10px] text-[var(--lumyn-banks)] font-medium"
                          data-testid={`bank-usage-count-${b.bank_id}`}
                        >
                          <Link2 className="w-3 h-3" />
                          Used in {b.usage_count} quiz{b.usage_count === 1 ? '' : 'zes'}
                        </div>
                      )}
                      {b.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {b.tags.slice(0, 3).map(t => (
                            <span
                              key={t}
                              className="inline-flex items-center gap-0.5 text-[10px] bg-[var(--lumyn-banks)]/10 text-[var(--lumyn-banks)] px-1.5 py-0.5 rounded"
                            >
                              <Tag className="w-2.5 h-2.5" />{t}
                            </span>
                          ))}
                          {b.tags.length > 3 && (
                            <span className="text-[10px] text-muted-foreground">+{b.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </button>

                {/* Kebab — floats over the card's top-right */}
                <div className="absolute top-3 right-2 z-10">
                  <BankActionsMenu
                    bank={b}
                    onEdit={onEdit}
                    onDuplicate={onDuplicate}
                    onRetire={onRetire}
                    onDelete={onDelete}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
